import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  SCHEDULE_SUB_SLUGS,
  overviewTargetPath,
  type OverviewAction,
  type OverviewFacts,
  type OverviewNextAction,
} from '@scoutaround/script-breakdown-shared';

import type { ScheduleWarning } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { shownAsNextAction } from './ActionCenter.js';

/**
 * WHAT ONE PART OF THE PRODUCTION HAS PUT IN DOUBT IN ANOTHER.
 *
 * Only what nothing else on the page says (redesign of 2026-10-30). That the two
 * workflows wait for the scene list is the workflow strip's own note and the next
 * action's whole point, so it is not repeated here; and with nothing to report
 * and nothing that failed, the section is not drawn at all.
 *
 * The action list says what to do; this says why, as a chain: a scene changed,
 * so these profiles and these breakdowns reopened. Every count is one the API
 * traced through a real relationship (`requirement_scene`, the scenes' own review
 * gates). Where the workspace cannot trace further — a script comparison's open
 * impacts have no screen to resolve them yet — it says that instead of a number
 * it does not have.
 */
export function Dependencies({
  facts,
  actions,
  next,
  address,
  scheduleWarnings,
  scheduleUnavailable,
  onRetry,
}: {
  facts: OverviewFacts;
  actions: readonly OverviewAction[];
  next: OverviewNextAction;
  address: string;
  /** Undefined while loading or where the plan has no schedule. */
  scheduleWarnings: readonly ScheduleWarning[] | undefined;
  /** The schedule could not be checked — said, not shown as "no warnings". */
  scheduleUnavailable: boolean;
  onRetry: () => void;
}) {
  const items: ReactNode[] = [];
  const scenes = facts.scenes.status === 'OK' ? facts.scenes.value : null;
  const L = facts.locations.status === 'OK' ? facts.locations.value : null;
  const B = facts.breakdown.status === 'OK' ? facts.breakdown.value : null;
  const deps = facts.dependencies.status === 'OK' ? facts.dependencies.value : null;
  const action = (id: string) => actions.find((a) => a.id === id);

  // ── A scene changed, and what it reached ──
  const reached = [
    deps !== null && deps.locationProfilesReopened > 0
      ? `${plural(deps.locationProfilesReopened, 'location profile', 'location profiles')} reopened for review`
      : null,
    L !== null && L.needsRecheck > 0
      ? `${plural(L.needsRecheck, 'location requirement', 'location requirements')} flagged for a re-check`
      : null,
    B !== null && B.scenesReopened > 0 ? `${plural(B.scenesReopened, 'scene breakdown', 'scene breakdowns')} reopened` : null,
  ].filter((r): r is string => r !== null);

  // A scene added after the analysis is in the same chain: nothing was read from it.
  if (scenes !== null && scenes.unread > 0) {
    reached.push(`${plural(scenes.unread, 'added scene has', 'added scenes have')} not been read by the AI`);
  }

  const changed =
    action('dependencies.scenes-changed') ??
    action('analysis.scenes-unread') ??
    action('locations.requirements-to-recheck') ??
    action('breakdown.scenes-reopened');
  // The next action already says that scenes changed; repeat it here only for
  // the chain it adds — what the change reached.
  const saidByNext = changed !== undefined && shownAsNextAction(changed, next, address) && reached.length === 0;

  if (((scenes !== null && scenes.outOfDate > 0) || reached.length > 0) && !saidByNext) {
    items.push(
      <Issue
        key="scene-change"
        testId="overview-dependency-scene-change"
        icon="alert"
        title={
          scenes !== null && scenes.outOfDate > 0
            ? `${plural(scenes.outOfDate, 'scene', 'scenes')} changed after the AI analysis`
            : scenes !== null && scenes.unread > 0
              ? `${plural(scenes.unread, 'scene was', 'scenes were')} added after the AI analysis`
              : 'Earlier reviews were reopened by a change'
        }
        link={changed === undefined ? null : { to: overviewTargetPath(address, changed.cta.target), label: 'Review impact' }}
      >
        <p style={{ margin: '4px 0 0' }} className="cc-note">
          {scenes !== null && scenes.outOfDate > 0
            ? 'What the AI suggested for them is kept, but it describes each scene as it was.'
            : 'Neither workflow covers a scene until the AI reads it or someone breaks it down by hand.'}
        </p>
        {reached.length > 0 ? (
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
            {reached.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        ) : null}
      </Issue>,
    );
  }

  // ── Requirements read from an earlier draft ──
  if (deps !== null && deps.requirementsFromEarlierDraft > 0 && facts.screenplay.status === 'OK' && facts.screenplay.value !== null) {
    items.push(
      <Issue
        key="earlier-draft"
        testId="overview-dependency-earlier-draft"
        icon="script"
        title={`${plural(deps.requirementsFromEarlierDraft, 'location requirement was', 'location requirements were')} read from an earlier draft`}
        link={{
          to: overviewTargetPath(address, { kind: 'TAB', tab: 'SCRIPT_AND_SCENES', sub: 'review' }),
          label: 'Check requirements',
        }}
      >
        <p style={{ margin: '4px 0 0' }} className="cc-note">
          Draft v{facts.screenplay.value.versionNumber} is current. They stand until someone reviews them against it.
        </p>
      </Issue>,
    );
  }

  // ── A script comparison's open impacts: counted, with no screen to resolve them ──
  if (deps !== null && deps.openImpacts.blocker + deps.openImpacts.warning > 0) {
    items.push(
      <Issue
        key="impacts"
        testId="overview-dependency-impacts"
        icon="nodes"
        title={
          deps.openImpacts.blocker > 0
            ? 'Matching is paused by a script comparison'
            : `${plural(deps.openImpacts.warning, 'scene change', 'scene changes')} from a script comparison to check`
        }
        link={null}
      >
        <p style={{ margin: '4px 0 0' }} className="cc-note">
          {deps.openImpacts.blocker > 0
            ? `${plural(deps.openImpacts.blocker, 'change waits', 'changes wait')} for a decision before locations can be searched again.`
            : 'They do not block anything.'}{' '}
          Resolving them is not available in the workspace yet; ask ScoutAround if this stays.
        </p>
      </Issue>,
    );
  }

  // ── The schedule ──
  if (scheduleWarnings !== undefined && scheduleWarnings.length > 0) {
    items.push(
      <Issue
        key="schedule"
        testId="overview-dependency-schedule"
        icon="flag"
        title={`${plural(scheduleWarnings.length, 'shooting day uses', 'shooting days use')} a previous primary location`}
        link={{
          to: overviewTargetPath(address, { kind: 'TAB', tab: 'SCHEDULE', sub: SCHEDULE_SUB_SLUGS.RED_FLAGS }),
          label: 'Open red flags',
        }}
      >
        <details>
          <summary>Show the days</summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
            {scheduleWarnings.map((w) => (
              <li key={w.assignmentId}>{w.message}</li>
            ))}
          </ul>
        </details>
      </Issue>,
    );
  }

  const failed = facts.dependencies.status === 'ERROR' || scheduleUnavailable;
  if (items.length === 0 && !failed) return null;

  return (
    <section className="cc-panel" data-testid="overview-dependencies" aria-labelledby="overview-dependencies-title">
      <div className="cc-panel-head">
        <h2 className="cc-panel-title" id="overview-dependencies-title">
          <Icon name="nodes" size={18} />
          Dependencies & issues
        </h2>
        {items.length > 0 ? <span className="cc-panel-sub">{String(items.length)}</span> : null}
      </div>

      {facts.dependencies.status === 'ERROR' ? (
        <p className="cc-note" data-testid="overview-dependencies-error" style={{ margin: '0 0 8px' }}>
          Some dependencies could not be checked.{' '}
          <button type="button" className="ctl" style={{ marginLeft: 6 }} onClick={onRetry} data-testid="overview-dependencies-retry">
            Try again
          </button>
        </p>
      ) : null}
      {scheduleUnavailable ? (
        <p className="cc-note" data-testid="overview-dependencies-schedule-error" style={{ margin: '0 0 8px' }}>
          The schedule could not be checked just now.
        </p>
      ) : null}

      {items.length === 0 ? null : <ul className="cc-issues">{items}</ul>}
    </section>
  );
}

function Issue({
  testId,
  icon,
  title,
  link,
  children,
}: {
  testId: string;
  icon: 'alert' | 'script' | 'nodes' | 'flag';
  title: string;
  link: { to: string; label: string } | null;
  children: ReactNode;
}) {
  return (
    <li className="cc-issue" data-testid={testId}>
      <p className="cc-issue-title">
        <Icon name={icon} size={15} />
        {title}
      </p>
      {children}
      {link === null ? null : (
        <Link to={link.to} className="cc-link" style={{ marginTop: 6 }} data-testid={`${testId}-link`}>
          {link.label}
          <Icon name="arrowRight" size={14} />
        </Link>
      )}
    </li>
  );
}

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}
